import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import './ChatbotBuilder.css';

function ChatbotBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [targetUserId, setTargetUserId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    system_prompt: 'You are a helpful AI assistant. Be friendly, professional, and concise in your responses.',
    model_provider: 'groq',
    model_name: 'llama-3.3-70b-versatile',
    temperature: 0.7,
    max_tokens: 1000,
    is_active: true,
    category_id: '',
    welcome_message: 'Hi there! 👋 Welcome! Feel free to look around and ask me anything.',
    primary_language: 'auto',
    voice_enabled: false,
    tts_provider: 'browser',
    tts_voice: 'nova',
    tts_speed: 1.0,
    stt_languages: [],   // [] = auto-detect; ['en','bn'] = pin/hint list
  });

  useEffect(() => {
    if (id) fetchChatbot();
    if (isAdmin && !id) fetchUsers();
    fetchCategories();
  }, [id, isAdmin]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/admin/users');
      setUsers(res.data.users);
    } catch {
      // non-critical
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/api/categories');
      setCategories(res.data.categories);
    } catch {
      // non-critical
    }
  };

  const fetchChatbot = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/chatbots/${id}`);
      const chatbot = response.data.chatbot;
      setFormData({
        name: chatbot.name || '',
        description: chatbot.description || '',
        system_prompt: chatbot.system_prompt || '',
        model_provider: chatbot.model_provider || 'groq',
        model_name: chatbot.model_name || 'llama-3.3-70b-versatile',
        temperature: chatbot.temperature || 0.7,
        max_tokens: chatbot.max_tokens || 1000,
        is_active: chatbot.is_active !== undefined ? chatbot.is_active : true,
        category_id: chatbot.category_id || '',
        welcome_message: chatbot.config?.welcome_message || 'Hi there! 👋 Welcome! Feel free to look around and ask me anything.',
        primary_language: chatbot.config?.primary_language || 'auto',
        voice_enabled: chatbot.config?.voice_enabled || false,
        tts_provider: chatbot.config?.tts_provider || 'browser',
        tts_voice: chatbot.config?.tts_voice || 'nova',
        tts_speed: chatbot.config?.tts_speed || 1.0,
        stt_languages: chatbot.config?.stt_languages || [],
      });
    } catch (error) {
      toast.error('Failed to load chatbot');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value),
      };
      // When TTS provider changes, reset voice to a sensible default for that provider
      if (name === 'tts_provider') {
        if (value === 'openai')  next.tts_voice = ['alloy','echo','fable','onyx','nova','shimmer'].includes(prev.tts_voice) ? prev.tts_voice : 'nova';
        if (value === 'browser') next.tts_voice = '';
      }
      return next;
    });
  };

  // Toggle a language code in the stt_languages array
  const handleSttLangToggle = (code, checked) => {
    setFormData(prev => ({
      ...prev,
      stt_languages: checked
        ? [...(prev.stt_languages || []), code]
        : (prev.stt_languages || []).filter(l => l !== code),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { welcome_message, primary_language, voice_enabled, tts_provider, tts_voice, tts_speed, stt_languages, ...rest } = formData;
      const cleanedData = {
        ...rest,
        category_id: formData.category_id ? parseInt(formData.category_id, 10) : null,
        config: { welcome_message, primary_language, voice_enabled, tts_provider, tts_voice, tts_speed, stt_languages },
      };
      if (id) {
        await axios.put(`/api/chatbots/${id}`, cleanedData);
        toast.success('Chatbot updated successfully!');
      } else {
        const payload = { ...cleanedData };
        if (isAdmin && targetUserId) payload.target_user_id = targetUserId;
        const response = await axios.post('/api/chatbots', payload);

        toast.success('Chatbot created successfully!');
        navigate(`/builder/${response.data.chatbot.id}`);
      }
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.error === 'limit_exceeded'
        ? data.message
        : (data?.message || 'Failed to save chatbot');
      toast.error(msg, { duration: data?.error === 'limit_exceeded' ? 7000 : 4000 });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="builder-container">
      <div className="container">
        <button onClick={() => navigate('/dashboard')} className="back-button">
          <ArrowLeftIcon className="icon" />
          Back to Dashboard
        </button>

        <div className="builder-card">
          <h1>{id ? 'Edit Chatbot' : 'Create New Chatbot'}</h1>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Chatbot Name *</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="My AI Assistant"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <input
                type="text"
                name="description"
                className="form-input"
                value={formData.description}
                onChange={handleChange}
                placeholder="A brief description of your chatbot"
              />
            </div>

            {categories.length > 0 && (
              <div className="form-group">
                <label className="form-label">Bot Category</label>
                <select
                  name="category_id"
                  className="form-input"
                  value={formData.category_id}
                  onChange={handleChange}
                >
                  <option value="">— General (no category) —</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                  Category adds specialized knowledge to this bot's context (e.g. Travel Agent adds Schengen visa rules)
                </small>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                Response Language
                <span className="lang-required-badge">affects LLM + voice</span>
              </label>
              <select
                name="primary_language"
                className="form-input"
                value={formData.primary_language}
                onChange={handleChange}
              >
                <option value="auto">🌐 Auto-detect — mirror the user's language</option>
                <option value="bn">🇧🇩 Bengali / Banglish (বাংলা)</option>
                <option value="en">🇬🇧 English</option>
                <option value="ar">🇸🇦 Arabic (العربية)</option>
                <option value="hi">🇮🇳 Hindi (हिन्दी)</option>
                <option value="ur">🇵🇰 Urdu (اردو)</option>
                <option value="tr">🇹🇷 Turkish (Türkçe)</option>
                <option value="fr">🇫🇷 French (Français)</option>
                <option value="es">🇪🇸 Spanish (Español)</option>
                <option value="de">🇩🇪 German (Deutsch)</option>
                <option value="zh">🇨🇳 Chinese (中文)</option>
              </select>

              <div className="lang-info-box">
                {formData.primary_language === 'auto' ? (
                  <p>
                    <strong>Auto mode:</strong> the LLM detects the user's language from each message and replies in the same language. Good for multilingual audiences.
                  </p>
                ) : (
                  <p>
                    <strong>Fixed language mode:</strong> every reply will be forced into the selected language — even if the user writes in English. This also pins the voice transcription (Whisper) to the same language, preventing mis-detections like Bengali being read as Sinhala.
                  </p>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Voice Chat</label>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  name="voice_enabled"
                  checked={formData.voice_enabled}
                  onChange={handleChange}
                />
                <span>Enable voice input &amp; speech responses</span>
              </label>
              {formData.voice_enabled && (
                <div className="voice-settings">
                  {/* ── Transcription language picker ─────────────────────── */}
                  <div className="voice-settings-row">
                    <label className="voice-settings-label">
                      Voice input languages
                      {(formData.stt_languages || []).length === 1 && (
                        <span className="stt-pin-badge">pinned</span>
                      )}
                      {(formData.stt_languages || []).length > 1 && (
                        <span className="stt-auto-badge">auto-detect</span>
                      )}
                    </label>
                    <div className="stt-lang-grid">
                      {[
                        { code: 'en', flag: '🇬🇧', label: 'English' },
                        { code: 'bn', flag: '🇧🇩', label: 'Bengali' },
                        { code: 'ar', flag: '🇸🇦', label: 'Arabic' },
                        { code: 'hi', flag: '🇮🇳', label: 'Hindi' },
                        { code: 'ur', flag: '🇵🇰', label: 'Urdu' },
                        { code: 'tr', flag: '🇹🇷', label: 'Turkish' },
                        { code: 'fr', flag: '🇫🇷', label: 'French' },
                        { code: 'es', flag: '🇪🇸', label: 'Spanish' },
                        { code: 'de', flag: '🇩🇪', label: 'German' },
                        { code: 'zh', flag: '🇨🇳', label: 'Chinese' },
                      ].map(({ code, flag, label }) => (
                        <label key={code} className="stt-lang-chip">
                          <input
                            type="checkbox"
                            checked={(formData.stt_languages || []).includes(code)}
                            onChange={e => handleSttLangToggle(code, e.target.checked)}
                          />
                          <span>{flag} {label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="stt-lang-hint">
                      {(formData.stt_languages || []).length === 0 && (
                        <>⚡ <strong>Auto-detect</strong> — Whisper picks the language. Select exactly one for best accuracy (prevents mis-detection like Bengali → Sinhala).</>
                      )}
                      {(formData.stt_languages || []).length === 1 && (
                        <>✅ <strong>Pinned</strong> — Whisper is locked to this language. Most accurate; eliminates cross-language confusion.</>
                      )}
                      {(formData.stt_languages || []).length > 1 && (
                        <>🌐 <strong>Auto-detect</strong> — Whisper picks the best match from all languages. (Whisper doesn't support a restrict-to list, so 2+ selections = auto-detect.)</>
                      )}
                    </p>
                  </div>

                  {/* ── TTS provider ──────────────────────────────────────── */}
                  <div className="voice-settings-row">
                    <label className="voice-settings-label">Text-to-speech</label>
                    <select
                      name="tts_provider"
                      className="form-input"
                      value={formData.tts_provider}
                      onChange={handleChange}
                    >
                      <option value="browser">Browser voices (free, built-in)</option>
                      <option value="openai">OpenAI TTS (natural quality, needs API key)</option>
                      <option value="none">None — voice input only, text response</option>
                    </select>
                  </div>

                  {formData.tts_provider === 'openai' && (
                    <div className="voice-settings-row">
                      <label className="voice-settings-label">OpenAI voice</label>
                      <select
                        name="tts_voice"
                        className="form-input"
                        value={formData.tts_voice}
                        onChange={handleChange}
                      >
                        {['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'].map(v => (
                          <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="voice-settings-row">
                    <label className="voice-settings-label">Speed — {formData.tts_speed}×</label>
                    <input
                      type="range"
                      name="tts_speed"
                      min="0.5"
                      max="2.0"
                      step="0.1"
                      value={formData.tts_speed}
                      onChange={handleChange}
                      className="voice-speed-slider"
                    />
                  </div>
                </div>
              )}
              <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                Speech recognition via Groq Whisper (100+ languages, free with your GROQ_API_KEY).
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Welcome Message</label>
              <textarea
                name="welcome_message"
                className="form-textarea"
                value={formData.welcome_message}
                onChange={handleChange}
                rows={2}
                placeholder="e.g. Hi there! 👋 Welcome! Feel free to look around and ask me anything."
              />
              <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                Shown as the bot's first message when the chat widget opens. Leave blank to hide it.
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">System Prompt</label>
              <textarea
                name="system_prompt"
                className="form-textarea"
                value={formData.system_prompt}
                onChange={handleChange}
                rows={6}
                placeholder="Define the personality and behavior of your chatbot..."
              />
              <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                This prompt sets the personality and behavior of your chatbot
              </small>
            </div>

            {isAdmin && (
              <div className="admin-section">
                <div className="admin-section-label">Admin Settings</div>

                {!id && users.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Assign to User</label>
                    <select
                      className="form-input"
                      value={targetUserId}
                      onChange={e => setTargetUserId(e.target.value)}
                    >
                      <option value="">— My account —</option>
                      {users
                        .filter(u => u.id !== user.id)
                        .map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name ? `${u.name} (${u.email})` : u.email} — {u.plan}
                          </option>
                        ))}
                    </select>
                    <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                      Leave blank to create under your own account
                    </small>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Model Provider</label>
                    <select
                      name="model_provider"
                      className="form-input"
                      value={formData.model_provider}
                      onChange={handleChange}
                    >
                      <option value="groq">Groq (Fast & Free)</option>
                      <option value="openai">OpenAI</option>
                      <option value="openai-compatible">OpenAI Compatible</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Model Name</label>
                    <input
                      type="text"
                      name="model_name"
                      className="form-input"
                      value={formData.model_name}
                      onChange={handleChange}
                      placeholder="groq/compound-mini"
                    />
                    <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                      Recommended: <strong>groq/compound-mini</strong> (fast, 131K context). Use <strong>qwen/qwen3.8-27b</strong> for best multilingual support (Bengali, Arabic, German…).
                    </small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Temperature: {formData.temperature}</label>
                    <input
                      type="range"
                      name="temperature"
                      min="0"
                      max="2"
                      step="0.1"
                      value={formData.temperature}
                      onChange={handleChange}
                      className="form-range"
                    />
                    <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                      Lower = more focused, Higher = more creative
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max Tokens</label>
                    <input
                      type="number"
                      name="max_tokens"
                      className="form-input"
                      value={formData.max_tokens}
                      onChange={handleChange}
                      min="100"
                      max="4000"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                Active (Chatbot is live and can receive messages)
              </label>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : id ? 'Update Chatbot' : 'Create Chatbot'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChatbotBuilder;



